import React from 'react';
import { MapPinIcon } from '@/components/icons';
import { getSignedMediaUrl } from '@/lib/utils';

interface HeroSectionProps {
  event: {
    name: string;
    description: string;
    card_image_id?: string | null;
    base_image_id?: string | null;
    base_video_id?: string | null;
  };
  business?: {
    name: string;
    location?: {
      city: string;
    };
    card_image_id?: string | null;
    base_image_id?: string | null;
    base_video_id?: string | null;
  } | null;
  className?: string; // Add this
}

const HeroSection: React.FC<HeroSectionProps> = ({ event, business, className }) => {
  // Get the event media URL and type
  const getEventMedia = () => {
    // Event priority: base_video -> base_image -> card_image
    if (event?.base_video_id && event.base_video_id !== '00000000-0000-0000-0000-000000000000') {
      return { url: getSignedMediaUrl(event.base_video_id, 'video').url, isVideo: true };
    }
    if (event?.base_image_id && event.base_image_id !== '00000000-0000-0000-0000-000000000000') {
      return { url: getSignedMediaUrl(event.base_image_id, 'image').url, isVideo: false };
    }
    if (event?.card_image_id && event.card_image_id !== '00000000-0000-0000-0000-000000000000') {
      const isVideo = event.card_image_id.includes('video') || event.card_image_id.includes('mp4') || event.card_image_id.includes('webm');
      return {
        url: getSignedMediaUrl(event.card_image_id, isVideo ? 'video' : 'image').url,
        isVideo
      };
    }

    // Business priority: base_video -> base_image -> card_image
    if (business?.base_video_id && business.base_video_id !== '00000000-0000-0000-0000-000000000000') {
      return { url: getSignedMediaUrl(business.base_video_id, 'video').url, isVideo: true };
    }
    if (business?.base_image_id && business.base_image_id !== '00000000-0000-0000-0000-000000000000') {
      return { url: getSignedMediaUrl(business.base_image_id, 'image').url, isVideo: false };
    }
    if (business?.card_image_id && business.card_image_id !== '00000000-0000-0000-0000-000000000000') {
      const isVideo = business.card_image_id.includes('video') || business.card_image_id.includes('mp4') || business.card_image_id.includes('webm');
      return {
        url: getSignedMediaUrl(business.card_image_id, isVideo ? 'video' : 'image').url,
        isVideo
      };
    }
    return null;
  };

  const eventMedia = getEventMedia();

  return (
    <div className={className || "relative h-[40vh] min-h-[300px] max-h-[500px]"}>
      {eventMedia ? (
        eventMedia.isVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              // Fallback to gradient if video fails
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          >
            <source src={eventMedia.url} type="video/mp4" />
            <source src={eventMedia.url} type="video/webm" />
          </video>
        ) : (
          <img
            src={eventMedia.url}
            alt={event.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              // Fallback to gradient if image fails
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        )
      ) : null}
      
      {/* Fallback gradient background */}
      <div 
        className={`absolute inset-0 bg-cover bg-center ${eventMedia ? 'hidden' : ''}`}
        style={{ 
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      />
      
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
      <div className="relative w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] h-full flex items-end pb-8">
        <div className="space-y-2">
          <div className="flex items-center space-x-3 text-blue-400 font-medium">
            <MapPinIcon />
            <span>{business ? `${business.name}, ${business.location?.city}` : 'Event Location'}</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-serif font-bold text-white tracking-tight drop-shadow-lg">
            {event?.name || 'Event'}
          </h1>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
